import { Stack, Typography, type TypographyProps } from '@mui/material';
import type { SaleMode } from '@/types/domain';
import { formatMoney } from '@/utils/format';

export interface PriceDisplayProps {
  priceCents: number;
  salePriceCents?: number | null;
  currency: string;
  size?: 'small' | 'medium' | 'large';
  showCurrencyCode?: boolean;
  /** Products sold by monthly subscription (alone or as well as one-off). */
  saleMode?: SaleMode;
  subscriptionPriceCents?: number | null;
}

/** The price in one line of text, e.g. "$29.00", "$9.00/month" or "$29.00 or $9.00/month". */
export function priceText(p: {
  price_cents: number;
  sale_price_cents: number | null;
  currency: string;
  sale_mode?: SaleMode;
  subscription_price_cents?: number | null;
}): string {
  const monthly = p.sale_mode && p.sale_mode !== 'one_off' && p.subscription_price_cents ? `${formatMoney(p.subscription_price_cents, p.currency)}/month` : null;
  if (p.sale_mode === 'subscription' && monthly) return monthly;
  const effective = p.sale_price_cents ?? p.price_cents;
  const oneOff = effective === 0 ? 'Free' : formatMoney(effective, p.currency);
  return monthly ? `${oneOff} or ${monthly}` : oneOff;
}

export function PriceDisplay({ priceCents, salePriceCents, currency, size = 'medium', showCurrencyCode, saleMode = 'one_off', subscriptionPriceCents }: PriceDisplayProps) {
  const effective = salePriceCents ?? priceCents;
  const variant: TypographyProps['variant'] = size === 'large' ? 'h3' : size === 'small' ? 'subtitle1' : 'h6';
  const monthly = saleMode !== 'one_off' && subscriptionPriceCents ? subscriptionPriceCents : null;
  const perMonth = (
    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.25 }}>
      /month
    </Typography>
  );
  if (saleMode === 'subscription' && monthly) {
    return (
      <Typography variant={variant} sx={{ fontWeight: 700 }}>
        {formatMoney(monthly, currency)}
        {perMonth}
        {showCurrencyCode && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            {currency}
          </Typography>
        )}
      </Typography>
    );
  }
  const orMonthly = monthly != null && (
    <Typography variant="body2" color="text.secondary">
      or {formatMoney(monthly, currency)}/month
    </Typography>
  );
  if (effective === 0) {
    return (
      <Typography variant={variant} color="success.main" sx={{ fontWeight: 700 }}>
        Free
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, '& > *': { m: '0 !important' } }}>
      <Typography variant={variant} sx={{ fontWeight: 700 }}>
        {formatMoney(effective, currency)}
        {showCurrencyCode && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            {currency}
          </Typography>
        )}
      </Typography>
      {salePriceCents != null && salePriceCents < priceCents && (
        <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }} aria-label={`Was ${formatMoney(priceCents, currency)}`}>
          {formatMoney(priceCents, currency)}
        </Typography>
      )}
      {orMonthly}
    </Stack>
  );
}
